import React, { useState, useEffect } from "react";
import axios from "axios";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import "./DemoRequestForm.css";

interface DemoRequestData {
  firstName: string;
  lastName: string;
  email: string;
  company: string;
  phone: string;
  country: string;
  password: string;
  confirmPassword: string;
}

const DemoRequestForm: React.FC = () => {
  const [formData, setFormData] = useState<DemoRequestData>({
    firstName: "",
    lastName: "",
    email: "",
    company: "",
    phone: "",
    country: "",
    password: "",
    confirmPassword: ""
  });

  const [status, setStatus] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
  const [emailError, setEmailError] = useState<string>("");
  const [passwordErrors, setPasswordErrors] = useState<string[]>([]);
  const [confirmPasswordError, setConfirmPasswordError] = useState<string>("");
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState<boolean>(false);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    if (name === "email") {
      validateEmail(value);
    }

    if (name === "password") {
      validatePassword(value);
      if (formData.confirmPassword) {
        validateConfirmPassword(formData.confirmPassword, value);
      }
    }

    if (name === "confirmPassword") {
      validateConfirmPassword(value, formData.password);
    }
  };

  const handlePhoneChange = (value: string, countryData: any) => {
    setFormData({
      ...formData,
      phone: value,
      country: countryData.name
    });
  };

  const GenericDomains = new Set([
    "gmail.com",
    "yahoo.com",
    "hotmail.com",
    "outlook.com",
    "live.com",
    "aol.com",
    "icloud.com",
    "protonmail.com",
    "mail.com",
    "gmx.com",
    "msn.com",
    "zoho.com",
    "yandex.com"
  ]);

  const validateEmail = (email: string): boolean => {
    if (!email) {
      setEmailError("Email is required.");
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setEmailError("Enter a valid enterprise Email ID.");
      return false;
    }

    const domain = email.substring(email.indexOf("@") + 1).toLowerCase();
    if (GenericDomains.has(domain)) {
      setEmailError("Please use an enterprise email address.");
      return false;
    }

    setEmailError("");
    return true;
  };

  const validatePassword = (password: string): boolean => {
    const errors: string[] = [];

    if (!password) {
      errors.push("Password is required.");
    } else {
      if (password.length < 8) {
        errors.push("At least 8 characters.");
      }
      if (!/[A-Z]/.test(password)) {
        errors.push("At least one uppercase letter.");
      }
      if (!/\d/.test(password)) {
        errors.push("At least one number.");
      }
      if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) {
        errors.push("At least one special character.");
      }
    }

    setPasswordErrors(errors);
    return errors.length === 0;
  };

  const validateConfirmPassword = (confirmPassword: string, password: string): boolean => {
    if (!confirmPassword) {
      setConfirmPasswordError("Please confirm your password.");
      return false;
    }
    if (confirmPassword !== password) {
      setConfirmPasswordError("Passwords do not match.");
      return false;
    }
    setConfirmPasswordError("");
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setStatus("");
    setIsSubmitted(false);

    const isEmailValid = validateEmail(formData.email);
    const isPasswordValid = validatePassword(formData.password);
    const isConfirmPasswordValid = validateConfirmPassword(formData.confirmPassword, formData.password);

    if (!isEmailValid || !isPasswordValid || !isConfirmPasswordValid) {
      setIsLoading(false);
      return;
    }

    const payload = {
      name: `${formData.firstName}`.trim(),
      lastName: formData.lastName,
      email: formData.email,
      phoneNumber: formData.phone,
      country: formData.country,
      company: formData.company,
      passwords: formData.password
    };

    try {
      await axios.post(
        "https://araku-a3asedfccwdmbjbq.australiaeast-01.azurewebsites.net/api/demo/requestdemo",
        payload,
        {
          headers: {
            "Client-ID": "demo-client-id-123"
          }
        }
      );
      setIsSubmitted(true);
    } catch (error) {
      console.error(error);
      setStatus("âŒ Failed to submit request. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
  window.close();
};


  return (
    <div className="container">
      {!isSubmitted ? (
        <form onSubmit={handleSubmit} className="form-wrapper">
          <h2>Start Your 15-Day Free Trial Today</h2>
          <br />
          <fieldset>
            <legend>Please complete the form below to create your account.</legend>
              <div className="form-field">
                <input
                  type="text"
                  name="firstName"
                  placeholder="First Name"
                  value={formData.firstName}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="form-field">
                <input
                  type="text"
                  name="lastName"
                  placeholder="Last Name"
                  value={formData.lastName}
                  onChange={handleChange}
                  required
                />
              </div>
            <div className="form-field">
              <input
                type="email"
                name="email"
                placeholder="Email"
                value={formData.email}
                onChange={handleChange}
                required
              />
              {emailError && <p className="error">{emailError}</p>}
            </div>
            <div className="form-field">
              <input
                type="text"
                name="company"
                placeholder="Company"
                value={formData.company}
                onChange={handleChange}
              />
            </div>
          </fieldset>

          <fieldset>
            <div className="form-field">
              <PhoneInput
                country={"au"}
                value={formData.phone}
                onChange={handlePhoneChange}
                inputProps={{
                  required: true,
                  name: "phone",
                  autoFocus: false
                }}
                inputStyle={{ width: "100%" }}
              />
            </div>
          </fieldset>
          <fieldset>
            <div className="form-field">
              <div style={{ position: "relative", width: "100%" }}>
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  placeholder="Password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  style={{ width: "100%" }}
                />
                <span
                  onClick={() => setShowPassword(!showPassword)}
                  style={{
                    position: "absolute",
                    right: 10,
                    top: "50%",
                    transform: "translateY(-50%)",
                    cursor: "pointer"
                  }}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                     {showPassword ? "🔓" : "🔒"}
                </span>
              </div>
              {passwordErrors.length > 0 ? (
                <ul className="error">
                  {passwordErrors.map((error, index) => (
                    <li key={index}>{error}</li>
                  ))}
                </ul>
              ) : (
                formData.password && (
                  <p className="success"></p>
                )
              )}
            </div>
            <div className="form-field">
              <div style={{ position: "relative", width: "100%" }}>
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  name="confirmPassword"
                  placeholder="Confirm Password"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  required
                  style={{ width: "100%" }}
                />
                <span
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  style={{
                    position: "absolute",
                    right: 10,
                    top: "50%",
                    transform: "translateY(-50%)",
                    cursor: "pointer"
                  }}
                  aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                >
                     {showPassword ? "🔓" : "🔒"}
                </span>
              </div>
              {confirmPasswordError && <p className="error">{confirmPasswordError}</p>}
            </div>
          </fieldset>

          <div className="terms-container">
            <input type="checkbox" id="terms" className="terms" required />
            <label htmlFor="terms">
              I agree to the{" "}
              <a
                href="https://arakusoftware.com/terms-and-conditions"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: "#005eb8", textDecoration: "underline" }}
              >
                Terms &amp; Conditions
              </a>
              {" "}and consent to receive relevant communications.
            </label>
          </div>
          <button
            type="submit"
            className="submit-button"
            disabled={isLoading}
          >
            {isLoading ? "Submitting..." : "Start your 15-day free trial"}
          </button>

          {isLoading && (
            <p className="loading">⏳ Please wait, submitting your request...</p>
          )}
          {status && <p className="status">{status}</p>}
        </form>
      ) : (
        <div className="success-message">
          <h2>Thank you for your request.</h2>
          <p>
            You will shortly receive an email from <a href="mailto:admin@arakusoftware.com">admin@arakusoftware.com</a> with the details of your trial account.
            If you do not see the email in your inbox, please check your spam or junk folder.
          </p>
          <p>You may now close this page to continue browsing our website.</p>
          <button onClick={handleClose} className="close-button">
            Close
          </button>
        </div>
      )}
    </div>
  );
};
export default DemoRequestForm;